import React, {useMemo, useEffect} from "react";
import {inject, observer} from "mobx-react";
import SearchPreferencesHeader from "./components/SearchPreferencesHeader";
import {useTranslation} from "react-i18next";
import {ButtonTransparent} from "components/buttons/ButtonTransparent";
import {useNavigate} from "react-router-dom";
import {MAIN_PAGE, PROJECTS, SAVE_SEARCH_REGIONS} from "constants/routes.const";
import {showError} from "helpers/notifications.helper";
import {findSuppliersFilterTabs, suppliersFilterProps} from "constants/tabs.const";
import cn from "classnames";
import GroupSelections from "components/SearchPrefereces/GroupSelections";
import {serviceCategoriesSchema} from "components/SearchPrefereces/formAttrs";
import {Header} from "components/Header/Header";
import {useSavedFilters} from "helpers/userHooks";

const SearchServiceCategories = inject("store")(
  observer(({store: {organization, auth}}) => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {activeOrganizationId} = auth;

    const initCategories = useMemo(() => {
      const categories = {};
      for (const prop of suppliersFilterProps) {
        if (auth?.user?.ActiveOrganization?.SearchPreference[prop]) {
          categories[prop] = auth?.user?.ActiveOrganization?.SearchPreference[prop];
        }
      }
      return categories;
    }, [auth]);

    const [selectedCategories, setSelectedCategories] = useSavedFilters({
      key: "searchServiceCategories",
      defaultValue: {},
      initialValue: initCategories,
      storage: "local",
    });

    useEffect(() => {
      Object.entries(findSuppliersFilterTabs).forEach(([_, tab]) => {
        if (tab.content && tab.groupName) {
          const hasValues = tab.content.some(
            (select) => selectedCategories?.[select.label]?.length > 0
          );
          organization.searchPreferencesGroupSwitches[tab.groupName].isActive = hasValues;
        }
      });
      // eslint-disable-next-line
    }, [selectedCategories]);

    const handleContinue = async () => {
      try {
        await serviceCategoriesSchema.validate(selectedCategories);

        organization
          .saveSearchServiceCategories(selectedCategories)
          .then(() => {
            return organization
              .setOrganizationOnboardingStatus(activeOrganizationId, {
                onboardingSettings: {
                  isAvailableIn: true,
                  isServiceCategories: true,
                },
              })
              .then(() => navigate(PROJECTS))
              .catch(() => navigate(MAIN_PAGE));
          })
          .catch(() => {
            showError(t("ERROR_SAVING_CATEGORIES"));
          });
      } catch (error) {
        showError(t(error.message));
      }
    };

    const handleSetFieldValue = (field, value) => {
      const newCategories = {
        ...(selectedCategories || {}),
        [field]: value,
      };
      setSelectedCategories(newCategories);
    };

    return (
      <div className="search-categories-page">
        <Header addClass="settings-page__header" type="language" />
        <SearchPreferencesHeader
          title="SEARCH_PREFERENCES_HEADER_TITLE"
          description="SEARCH_SERVICE_CATEGORIES_PREFERENCES_HEADER_DESCRIPTION"
        />
        <div className="search-categories-content">
          <p className="search-categories-title">
            {t("SEARCH_SERVICE_CATEGORY_SELECT_LABEL")}
          </p>
          <p className="search-categories-tip">{t("SEARCH_SERVICE_CATEGORY_TIP")}</p>

          <div className="search-categories-selections">
            {findSuppliersFilterTabs.map(
              (item) =>
                item.visible && (
                  <GroupSelections
                    key={item.groupTitle}
                    title={t(item.groupTitle)}
                    name={item.groupName}
                    setFieldValue={handleSetFieldValue}
                    selects={item.content.map((select) => ({
                      list: select.array,
                      name: select.label,
                      mode: "multiple",
                      subTitle: select.groupSubTitle,
                      bottomElement: select.bottomElement,
                      values: selectedCategories?.[select.label],
                    }))}
                  />
                )
            )}
          </div>
        </div>
        <div className="search-categories-footer">
          <div className="search-categories-next">
            <span className={cn("next-text")}>
              {t("PREVIOUS")}: {t("WHERE_DO_YOU_OFFER_SERVICES")}
            </span>
            <ButtonTransparent
              name={t("BACK")}
              onClick={() =>
                navigate(SAVE_SEARCH_REGIONS, {
                  replace: true,
                  state: {renavigated: true},
                })
              }
              addClass="search-categories-back"
            />
            <ButtonTransparent
              name={t("FIRST_SETUP_CONTINUE")}
              addClass={cn("search-categories-continue", {
                __active: Object.keys(selectedCategories || {}).length,
              })}
              disabled={!Object.keys(selectedCategories || {}).length}
              onClick={handleContinue}
            />
          </div>
        </div>
      </div>
    );
  })
);

export default SearchServiceCategories;
